import { Female as FemaleIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { activityApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { differenceInDays } from "date-fns";
import { isEmpty, isNil } from "lodash";
import PatientDetailPageContainer from "pages/components/PatientDetailPageContainer";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";

import DashboardLayout from "../components/layouts/DashboardLayout";
import {
  MetricValueResponseDto,
  MetricValuesPaginationRequestDtoHealthProviderCodeEnum,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "../libs/cellera-sdk";

const DEFAULT_ROWS_PER_PAGE = [10, 25, 50, 100];

const ReproductiveHealthPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<MetricValueResponseDto[]>([]);
  const [total, setTotal] = useState(0);
  const [rowsPerPageList, setRowsPerPageList] = useState<number[]>([
    DEFAULT_ROWS_PER_PAGE[0],
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Define table columns
  const columns: CustomTableColumn<MetricValueResponseDto>[] = [
    {
      id: "startDate",
      label: "Start Date",
      getValue: (row) => formatDate(row.startDate),
    },
    {
      id: "endDate",
      label: "End Date",
      getValue: (row) => formatDate(row.endDate),
    },
    {
      id: "duration",
      label: "Duration (days)",
      getValue: (row) => getDuration(row.startDate, row.endDate),
    },
  ];

  const handleBack = () => {
    navigate("/patients");
  };

  const handleRefresh = () => {
    const fetchAndSetData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchData(patientId, page, rowsPerPage);
        setData(response.data);
        setTotal(Math.floor(response.pagination.total ?? 0 / rowsPerPage));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAndSetData();
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    if (newPage < 0) {
      setPage(0);
    } else if (newPage > total) {
      setPage(total);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = useCallback((date: Date) => {
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const getDuration = useCallback((startDate: Date, endDate: Date) => {
    return differenceInDays(endDate, startDate);
  }, []);

  const fetchData = useCallback(
    async (
      patientId: string | undefined,
      page: number,
      rowsPerPage: number
    ) => {
      if (isNil(patientId) || isEmpty(patientId)) {
        return { data: [], pagination: { total: 0 } };
      }
      const response = await activityApi.getMetricValuesPaginatedByPatientId({
        patientId,
        metricValuesPaginationRequestDto: {
          page: page + 1,
          pageSize: rowsPerPage,
          metricCode:
            MetricValuesPaginationRequestDtoMetricCodeEnum.Menstruation,
          healthProviderCode:
            MetricValuesPaginationRequestDtoHealthProviderCodeEnum.GoogleHealthConnect,
          orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
        },
      });
      return response;
    },
    []
  );

  useEffect(() => {
    const fetchAndSetData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchData(patientId, page, rowsPerPage);
        setData(response.data);
        setTotal(Math.floor(response.pagination.total ?? 0 / rowsPerPage));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAndSetData();
  }, [fetchData, page, patientId, rowsPerPage]);

  useEffect(() => {
    if (total === 0) {
      setRowsPerPageList([DEFAULT_ROWS_PER_PAGE[0]]);
      return;
    }
    const firstMatch =
      DEFAULT_ROWS_PER_PAGE.find((item) => item >= total) ??
      DEFAULT_ROWS_PER_PAGE[0];
    setRowsPerPageList(
      DEFAULT_ROWS_PER_PAGE.filter((item) => item <= firstMatch)
    );
  }, [total]);

  return (
    <DashboardLayout title="Reproductive Health">
      <PatientDetailPageContainer
        breadcrumbs={[
          { label: "Dashboard", href: "/dashboard", order: 0 },
          { label: "Patient Management", href: "/patients", order: 1 },
          {
            label: "Patient Details",
            href: `/patients`,
            order: 2,
          },
        ]}
        lastBreadcrumbItem="Reproductive Health"
        title="Reproductive Health"
        icon={<FemaleIcon />}
        onBack={handleBack}
        onRefresh={handleRefresh}
      >
        {/* Data Table */}
        <CustomTable
          data={data}
          columns={columns}
          totalCount={total || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageList}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          isLoading={isLoading}
          loadingMessage="Loading reproductive health data..."
          emptyStateContent={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <FemaleIcon
                sx={{
                  color: "text.disabled",
                  fontSize: 48,
                  opacity: 0.5,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                }}
              >
                No reproductive health data available
              </Typography>
            </Box>
          }
          getRowKey={(row: MetricValueResponseDto) => row.metricId}
          tableContainerSx={{
            height: "calc(100% - 60px)",
          }}
        />
      </PatientDetailPageContainer>
    </DashboardLayout>
  );
};

export default ReproductiveHealthPage;
